import React, { useState } from "react";
import { getFromStorage, setToStorage } from "../utils/utils";
import Button from "./Button";
import DragAndDrop from "./DragAndDrop";
import InfoModal from "./InfoModal";

import Calendar, { DateObject } from "react-multi-date-picker";

const PurchaseInformation = ({
  purchaseInformation,
  setPurchaseInformation,
  goNext
}) => {
  const [validationError, setValidationError] = useState({
    fileError: "",
    receiptDateError: ""
  });
  const [openInfoModal, setOpenInfoModal] = useState(false);

  const handleUpload = (file) => {
    const initialFileUploaded = getFromStorage("initialFileUploaded");
    if (!initialFileUploaded) {
      setToStorage("initialFileUploaded", true);
      setOpenInfoModal(true);
    }

    setPurchaseInformation((prevState) => ({
      ...prevState,
      receipt: file
    }));
    setValidationError((prevState) => ({
      ...prevState,
      fileError: ""
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let isValid = true;
    const { receipt, receiptDate } = purchaseInformation;

    if (!receipt) {
      setValidationError((prevState) => ({
        ...prevState,
        fileError: "You must upload a receipt!"
      }));
      isValid = false;
    }

    if (!receiptDate) {
      setValidationError((prevState) => ({
        ...prevState,
        receiptDateError: "This field is required"
      }));
      isValid = false;
    }

    if (receiptDate) {
      if (receiptDate.year < 2023 || receiptDate.year > 2024) {
        setValidationError((prevState) => ({
          ...prevState,
          receiptDateError: "Date must be between 2023 and 2024"
        }));
        isValid = false;
      }
    }

    if (isValid) {
      goNext();
    }
  };

  const handleDobChange = (val) => {
    setValidationError((prevState) => ({
      ...prevState,
      receiptDateError: ""
    }));
    setPurchaseInformation((prevState) => ({
      ...prevState,
      receiptDate: val
    }));
  };

  return (
    <form className="w-full" onSubmit={handleSubmit}>
      <div className="w-full">
        <div className="w-full flex items-start justify-between">
          <div className="flex flex-col">
            <h2 className="text-sm font-bold text-[#3E3E3E] mb-0.5">
              Upload receipt*
            </h2>
            <p className="text-xs text-[#3E3E3E] tracking-wide mb-2.5">
              Please upload a photo of your receipt to provide proof of
              purchase. Ensure receipt number, products purchased and receipt
              number are clearly visible. Jpg, jpeg and png files under 10 MB
              only.
            </p>
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-5 h-5 text-primary cursor-pointer"
            onClick={() => setOpenInfoModal(true)}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
            />
          </svg>
        </div>
        <InfoModal open={openInfoModal} setOpen={setOpenInfoModal} />
        <DragAndDrop
          uploadedFile={purchaseInformation.receipt}
          setUploadedFile={(file) => {
            handleUpload(file);
          }}
          error={validationError.fileError}
          setError={(err) => {
            setValidationError((prevState) => ({
              ...prevState,
              fileError: err
            }));
          }}
          additionalClassNames="pb-0 w-full lg:w-500 mb-2.5"
        />
        <div className="my-6 w-full flex flex-col sm:flex-row gap-5 sm:gap-10">
          <div className="flex flex-col w-full max-w-xs">
            <div className="flex items-center  mb-1">
              <h2 className="text-sm font-bold text-[#3E3E3E] tracking-wide">
                Purchase date*
              </h2>
              <span className="text-xs font-regular text-secondary ml-1">
                (receipt date)
              </span>
            </div>
            <div className="flex flex-col w-full relative">
              <Calendar
                currentDate={new DateObject(new Date())}
                minDate={
                  new DateObject({
                    year: 2023,
                    month: 1,
                    day: 1
                  })
                }
                format={"DD/MM/YYYY"}
                placeholder={"DD/MM/YYYY"}
                numberOfMonths={1}
                range={false}
                maxDate={"2024/12/31"}
                value={purchaseInformation.receiptDate}
                onChange={handleDobChange}
                style={{
                  width: "100%",
                  boxShadow: `0 1px 2px 0 rgba(0,0,0,0.05)`,
                  height: "40px",
                  borderColor: validationError.receiptDateError
                    ? "red"
                    : "#999999",
                  borderRadius: "6px",
                  fontSize: "14px",
                  padding: "3px 10px",
                  cursor: "pointer"
                }}
              />
              {validationError.receiptDateError && (
                <p className="text-xs text-red-600 mt-1">
                  {validationError.receiptDateError}
                </p>
              )}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="#999"
                className="w-4 h-5 absolute top-[10px] right-[14px]"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <Button type="submit" title={"Continue"} additonalClasses={"mt-4"} />
    </form>
  );
};

export default PurchaseInformation;
